/**
 * Redux Store Configuration
 * 
 * Configures the Redux store with Redux Toolkit and Redux Persist
 */

import { configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';

// Import reducers
import authReducer from './slices/authSlice';
import complaintReducer from './slices/complaintSlice';
import officerReducer from './slices/officerSlice';
import settingsReducer from './slices/settingsSlice';
import notificationReducer from './slices/notificationSlice';
import uiReducer from './slices/uiSlice';
import dashboardReducer from './slices/dashboardSlice';

// Combine all reducers
const rootReducer = combineReducers({
  auth: authReducer,
  complaints: complaintReducer,
  officers: officerReducer,
  settings: settingsReducer,
  notifications: notificationReducer,
  ui: uiReducer,
  dashboard: dashboardReducer,
});

// Persist configuration
const persistConfig = {
  key: 'vigilance-root',
  version: 1,
  storage,
  whitelist: ['settings'], // Only persist settings (like theme, etc.)
  // 'auth' and 'dashboard' are removed from whitelist for security.
  // Data should be fetched fresh or managed by context/local storage carefully.
  blacklist: ['ui', 'dashboard', 'auth'],
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

// Create persistor
export const persistor = persistStore(store);

// Export types for TypeScript support (optional)
export const selectAuth = (state) => state.auth;
export const selectComplaints = (state) => state.complaints;
export const selectOfficers = (state) => state.officers;
export const selectSettings = (state) => state.settings;
export const selectNotifications = (state) => state.notifications;
export const selectUI = (state) => state.ui;
export const selectDashboard = (state) => state.dashboard;

export default store;

